import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import L, { LatLngExpression } from 'leaflet';
import {
    MapContainer,
    TileLayer,
    Marker,
    Popup,
    Tooltip,
    LayersControl,
    useMap,
    LayerGroup,
    ZoomControl
} from 'react-leaflet';

import 'leaflet.tilelayer.colorfilter';
import 'leaflet/dist/leaflet.css';
import '@changey/react-leaflet-markercluster/dist/styles.min.css';
import { MapMarkerPoint, getMapMarkerPoints } from 'pages/thermography/modules/mapMarkerPoints';
import { AppContext } from 'providers/AppProvider';
import { useState } from 'react';
import LeafletControl from './leaflet-control';
import { usePowerStationFilterStore } from 'pages/thermography/stores/PowerStationFilterStore';

import "App.css"
import { conditionPriority } from '../Utils';
import { useTranslation } from 'react-i18next';

const filterValidCoordinates = (data: MapMarkerPoint[]) =>
    data.filter(p => p.lat !== undefined && p.lng !== undefined && !isNaN(p.lat) && !isNaN(p.lng));

const Legend = () => {
    const { t } = useTranslation();
    const tt = (key: string) => t(key, { ns: 'translation' });

    return (

        <div
            style={{
                position: 'absolute',
                bottom: '10px',
                right: '10px',
                backgroundColor: 'white',
                padding: '10px',
                borderRadius: '5px',
                fontSize: '12px',
                boxShadow: '0 0 10px rgba(0,0,0,0.2)',
                zIndex: 1000
            }}
        >
            <strong>{tt('pages.thermography.powerStationMap.legendTitle')}</strong>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li><span style={{ color: '#1E90FF', fontWeight: 'bold' }}>●</span> {tt('pages.thermography.powerStationMap.activePoint')}</li>
                {/* Adicione outras legendas conforme necessário */}
            </ul>
        </div>
    );
};

interface ILayerComponentPros {
    data: MapMarkerPoint[],
    setSearchTerm: React.Dispatch<React.SetStateAction<string>>
}
const LayerComponent = ({ data, setSearchTerm }: ILayerComponentPros) => {
    const { t, i18n } = useTranslation();
    const tt = (key: string) => t(key, { ns: 'translation' });

    const filteredData = filterValidCoordinates(data);
    const [filteredPowerStation, setFilteredPowerStation] = useState(false);

    const getMarkerIcon = (level?: number) => {

        const colorMap: Record<number, any> = {
            //grey
            1: { darkColor: '#7a7a7a', lighColor: '#d4d4d4' },

            // 'Green'
            2: { darkColor: '#006400', lighColor: '#00FF7F' },

            //'yellow',
            3: { darkColor: '#b8860b', lighColor: '#FFFF66' },

            //'orange',
            4: { darkColor: '#cc5500', lighColor: '#ff9900' },

            //'red'
            5:
                { darkColor: '#8B0000', lighColor: '#FF6347' },

        };

        const riskColorsMap = level && colorMap[level] ? colorMap[level] : { darkColor: '#006400', lighColor: '#00FF7F' }
        // console.log(riskColorsMap)

        const gradientId = `grad-${level}`;

        return L.divIcon({
            className: 'custom-div-icon',
            iconSize: [20, 25], // Ajuste conforme necessário
            html: `
                <svg width="20" height="25" viewBox="0 0 100 130" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                        <linearGradient id="${gradientId}" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" style="stop-color:${riskColorsMap.darkColor}; stop-opacity:1" />
                            <stop offset="100%" style="stop-color:${riskColorsMap.lighColor}; stop-opacity:1" />
                        </linearGradient>
                    </defs>
                    <path d="M50 0
                            C22 0, 0 22, 0 50
                            C0 78, 50 130, 50 130
                            C50 130, 100 78, 100 50
                            C100 22, 78 0, 50 0
                            Z" fill="url(#${gradientId})" />
                    <circle cx="50" cy="50" r="20" fill="white"/>
                </svg>
            `
        });
    };
    const navigate = useNavigate();
    const map = useMap();
    const { config } = useContext(AppContext);
    const { theme } = config;

    const filter =
        theme === 'dark'
            ? [
                'invert:98%',
                'grayscale:69%',
                'bright:89%',
                'contrast:111%',
                'hue:205deg',
                'saturate:1000%'
            ]
            : ['bright:101%', 'contrast:101%', 'hue:23deg', 'saturate:225%'];

    useEffect(() => {
        map.invalidateSize();

        if (filteredData.length > 0) {
            const bounds = L.latLngBounds(filteredData.map(p => [p.lat, p.lng] as [number, number]));
            map.fitBounds(bounds, {
                padding: [50, 50],
                maxZoom: 16
            });
        }


    }, [filteredData, config]);

    useEffect(() => {
        if (map && theme === 'dark') {
            (L.tileLayer as any)
                .colorFilter('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
                    attribution: null,
                    transparent: true,
                    filter: filter
                })
                .addTo(map);
        }
    }, [theme]);

    return (
        <>
            {/* <Legend /> */}
            <LayersControl key={i18n.resolvedLanguage} position="topright">
                <LayersControl.BaseLayer name={tt('pages.thermography.powerStationMap.layers.streets')}> <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                </LayersControl.BaseLayer>


                < LayersControl.BaseLayer name={tt('pages.thermography.powerStationMap.layers.cartoLightWithFilter')} >
                    <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
                </LayersControl.BaseLayer >

                <LayersControl.BaseLayer checked name={tt('pages.thermography.powerStationMap.layers.satellite')}>
                    <TileLayer
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                        attribution="Tiles &copy; Esri"
                    />
                </LayersControl.BaseLayer>

                <LayerGroup>
                    {filteredData.map(marker => (
                        <Marker
                            key={marker.name}
                            position={[marker.lat, marker.lng]}
                            icon={getMarkerIcon(marker.anomalyLevel)}
                            // icon={getMarkerIcon(Math.floor(Math.random() * 5) + 1)}
                            eventHandlers={{
                                // evento de duplo clique no marker
                                dblclick: () => {
                                    // navega para a rota desejada
                                    //navigate(`/inspectionRoute`);
                                    setSearchTerm(filteredPowerStation ? '' : marker.code)
                                    setFilteredPowerStation(!filteredPowerStation);
                                }
                            }}
                        >
                            <Tooltip
                                permanent
                                direction="bottom"
                                offset={[0, 0]}
                                className='custom-tooltip'
                            >
                                {marker.name}
                            </Tooltip>
                            <Popup>
                                <h6 className="mb-1">{marker.code}</h6>
                            </Popup>
                        </Marker>
                    ))}
                </LayerGroup>
            </LayersControl >
        </>
    );
};

const PowerStationMap = ({ ...rest }) => {
    const { t } = useTranslation();
    const tt = (key: string) => t(key, { ns: 'translation' });
    const { config: { isRTL } } = useContext(AppContext);
    const position: LatLngExpression = [10.737, 0];
    const [searchTerm, setSearchTerm] = useState('');

    const mapMarkerPoints = usePowerStationFilterStore((state) => state.mapMarkerPoints);
    const [processedMarkerPoints, setProcessedMarkerPoints] = useState(mapMarkerPoints);

    // const setMapMarkerPoints = usePowerStationFilterStore((state) => state.setMapMarkerPoints);
    const activeAnomalyRecords = usePowerStationFilterStore((state) => state.activeAnomalyRecords);
    const filterPowerStations = usePowerStationFilterStore((state) => state.filterPowerStations);

    const conditionFilter = usePowerStationFilterStore((state) => state.conditionFilter);
    const deadlineFilter = usePowerStationFilterStore((state) => state.deadlineFilter);
    const monitoringFilter = usePowerStationFilterStore((state) => state.monitoringFilter);



    useEffect(() => {
        const filtered = conditionFilter.length > 0 || deadlineFilter.length > 0 || monitoringFilter.length > 0;


        // Filter mapMarkerPoints when filtered=true to only include points with anomalies in activeAnomalyRecords
        let filteredPoints = mapMarkerPoints;
        if (filtered) {
            filteredPoints = mapMarkerPoints.filter(marker =>
                activeAnomalyRecords?.some(
                    anomaly => anomaly.plant && anomaly.plant.id === marker.codigo
                )
            );
        }

        // Process the filtered list to update anomalyLevel based on active anomalies
        const processedList = filteredPoints.map(marker => {
            console.log("FilterStore, activeAnomalyRecords: " + activeAnomalyRecords.length);


            // Find all anomalies associated with this marker (via power_station_id)
            const associatedAnomalies = activeAnomalyRecords.filter(
                anomaly => anomaly.plant && anomaly.plant.id === marker.codigo
            );

            if (associatedAnomalies.length > 0) {
                // Calculate maximum risk level from all associated anomalies
                const maxRiskLevel = Math.max(
                    ...associatedAnomalies.map(anomaly =>
                        conditionPriority[anomaly.condition ?? ''] || 1
                    )
                );

                return {
                    ...marker,
                    anomalyLevel: maxRiskLevel
                };
            }

            // If no anomalies, keep original anomalyLevel or default to 1
            return {
                ...marker,
                anomalyLevel: marker.anomalyLevel || 1
            };
        });

        setProcessedMarkerPoints(processedList);

    }, [activeAnomalyRecords, mapMarkerPoints, conditionFilter, monitoringFilter, deadlineFilter]);

    useEffect(() => {
        filterPowerStations(searchTerm)
    }, [searchTerm])

    return (
        <MapContainer
            zoomControl={false}
            zoom={isRTL ? 1.8 : 1.5}
            minZoom={isRTL ? 1.8 : 1.3}
            zoomSnap={0.5}
            center={position}
            {...rest}
            className="h-100 bg-body-emphasis"
        >
            <LeafletControl position="topleft">
                <div className="leaflet-control-center">
                    <input
                        type="text"
                        placeholder={tt('pages.thermography.powerStationMap.searchPlaceholder')}
                        value={searchTerm}
                        onChange={e => {

                            setSearchTerm(e.target.value);
                        }
                        }
                        style={{ padding: '0.3rem', width: '180px' }}
                    />
                </div>
            </LeafletControl>
            <LayerComponent
                data={processedMarkerPoints}
                setSearchTerm={setSearchTerm} />

            <ZoomControl position="topright" />
        </MapContainer>
    );
};

export default PowerStationMap;
